import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import FileSaver from 'file-saver'
import HttpStatus from 'http-status-codes'
import appDisplay from 'scoreboard/components/app-display'
import conductor from 'scoreboard/components/conductor'
import errorHandler from 'scoreboard/components/error-handler'
import globals from 'scoreboard/components/globals'
import i18n from 'scoreboard/components/i18n'
import sections from 'scoreboard/components/sections'
import spectator from 'scoreboard/components/spectator'
import url from 'scoreboard/components/url'
import Xhr from 'scoreboard/components/xhr'
import setupErrors from 'scoreboard/components/setup-errors'
import ErrorView from 'scoreboard/components/error-view'
import ServerErrorDetailsDialog from 'scoreboard/components/server-error-details-dialog'
import SendToEmailForm from 'scoreboard/components/export/send-to-email-form'
import SendToEmailConfirmation from './send-to-email-confirmation'
import ExportForm from './export-form'
import ExportScheduledConfirmation from './export-scheduled-confirmation'
import ExportStatusDialog from './export-status-dialog'
import licenseRestriction from 'scoreboard/components/license-restriction'
import license from 'scoreboard/components/license'
import briefer from 'scoreboard/briefings/briefer'


function handleError(xhr, replace = true) {

	const displayError = (errorView) => {
		if (replace) {
			appDisplay.replaceDialog(errorView)
		} else {
			appDisplay.dialog(errorView)
		}
		return true
	}

	const forbiddenHandler = () => {
		if (xhr.responseJSON.messageCode == 'admin.licenses.error.export') {
			return displayError(licenseRestriction.exportRestriction(), true)
		}
	}

	errorHandler(xhr, {
		forbiddenHandler,
		handler: () => displayError(new ErrorView(
			setupErrors.errorViewOptions(xhr.status, xhr.responseJSON)))
	})
}

const sendToExport = {

	/**
	 * @typedef ExportOptions
	 *
	 * @property {String|Function} [options.exportURL=Backbone.history.location]
	 *
	 * @property {String|Function} [options.domainId]
	 * 		The domainId of the thing being exported, can provide an undefined value
	 * 		if it's an unsaved entity (e.g. report)
	 *
	 * @property {array} [options.options]
	 * 		each array item must be a key/value pair delimited by '='
	 * 		Example: ['full=true'] The server uses these options to determine
	 * 		specific routes for the export view. i.e. history period vs. full view
	 *
	 * @property {Boolean} [options.immediate=false] Generate export immediately (no download/schedule prompt)
	 *
	 * @property {Function} [options.excelFn]
	 * 		PDF based exports open a new window with an API request to generate the file.
	 * 		But Excel exports are generated in the browser, so the view must provide
	 * 		a function to initiate the export.  The convention is for the view
	 * 		to implement an `exportTable` function and pass it here already bound to the view
	 * 		along with any required arguments.
	 * 		This will only be invoked for "download now" exports.
	 * 		Scheduled excel exports are triggered automatically by `exportPreparer.prepare`
	 *
	 * @property {Function} [options.csvDownloadFn] If the Excel export is to a server
	 * generated CSV option instead.
	 *
	 * @property {Function} [options.validateFn]
	 * 		If this function is provided it will be invoked before the prompt is displayed.
	 * 		It can return a view that will be displayed instead of the prompt,
	 * 		allowing callers to provide a pre-validation so users don't see
	 * 		an export dialog, and then are presented with an error.
	 * 		(Originally added for dataset overview exports with no filters.)
	 *
	 * @property {Function} [options.runtimeOptionsFn]
	 * 		The (header) Backbone View specifies what export options to show.
	 * 		Most of which display a dialog with `export-form.js`.  If the state of
	 * 		the underlying view changes (e.g. data set views) then the options
	 * 		for the export form may also be impacted.  This function can be
	 * 		provided in order to override creation time options with runtime options
	 *
	 * @property {Boolean} [options.includeScheduledCount=true]
	 *
	 * @property {String} [options.showToggle=true]
	 * 		If false, no toggle will be shown, and the default 'Download' will be used
	 *
	 * @property {String} [options.instructions]
	 * 		Provide custom instructions for display in the banner
	 *
	 * @property {Function} [options.uiState] A function that returns an object that
	 * 		will be serialized and persisted in exports.state
	 *
	 * @property {String} [options.forPrint=false] If the export is for printing (send to Printer)
	 *
	 * @property {Boolean} [options.replaceDialog=true] Pass false to preserve any
	 * open dialogs.  Required if the thing being exported is in a dialog.
	 * (e.g. dataset template permissions)
	 *
	 */

	/**
	 *
	 * Open new window and make request to API server for the export file
	 * @param {String} type the type of export (pdf, powerpoint, word, png)
	 *
	 * @param {string} [chopAfter] the url is read from the Backbone.history, if
	 *     the API request should not match exactly what is in the url, this
	 *     parameter can be provided to truncate everything after this value.
	 *
	 * @param {ExportOptions} options
	 */
	prompt(type, chopAfter, options) {
		if (!license.export) {
			appDisplay.dialog(licenseRestriction.exportRestriction())
			return
		}

		const runtimeOptions = _.invoke(options, 'runtimeOptionsFn') || {}
		options = _.extend({}, {
			includeScheduledCount: true,
			showToggle: true
		}, options, runtimeOptions)

		const createForm = (count) => {
			const exportForm = new ExportForm(_.extend({
				fileType: type,
				count
			}, options))
			exportForm.on('download', () => this.download(type, chopAfter, options))
			exportForm.on('schedule', ({scheduleData, recipients, singlePeriod}) => {
				let urlOptions = _.extend({
					scheduled: true
				}, options)
				_setState(options)
				const data = JSON.stringify({ // ScheduledExport.kt
					domainId:  _.result(options, 'domainId'),
					domainType:  _.result(options, 'domainType'),
					url: _buildExportUrl(chopAfter, type, urlOptions, {label: false}),
					label: _exportLabel({addPeriod: false}),
					singlePeriod,
					scheduleData,
					timeZoneString: scheduleData.timeZone,
					recipients,
					state: options.state
				})
				$.post({
					url: 'api/exports', data,
					error: (xhr) => {
						handleError(xhr)
					}
				}).done(response => {
						exportForm.trigger('done')
						let scheduleData = _.get(response, 'scheduleData')
						appDisplay.dialog(new ExportScheduledConfirmation({
							nextRunTime: scheduleData.nextRunTime
						}))
					})
			})
			return exportForm
		}

		let errorView
		if (options.validateFn) {
			errorView = options.validateFn()
		}

		const id = conductor.id()
		if (errorView) {
			appDisplay.dialog(errorView)
		} else if (options.includeScheduledCount && id) { // To display in the banner
			const countPromise = $.get(`api/exports/${sections.toUrl(conductor.section())}/${id}/count`)
			appDisplay.dialog(countPromise, count => createForm(count))
		} else {
			appDisplay.dialog(createForm(0))
		}
	},

	/**
	 * Runs an export
	 * @see #prompt args
	 */
	download(type, chopAfter, options) {
		if (!license.export) {
			appDisplay.dialog(licenseRestriction.exportRestriction())
			return
		}

		if (type === 'excel' && options.excelFn) {
			this._downloadExcel(options)
		} else if (type === 'csv' && options.csvDownloadFn) {
			this._downloadCSV(options)
		} else {
			_setState(options)
			const url = _buildExportUrl(chopAfter, type, options)
			this.startServerDownload(url, options)
		}
	},

	// Excel Downloads are generated in the browser
	_downloadExcel(options) {
		console.time('_downloadExcel')
		const statusDialog = new ExportStatusDialog()
		if (_isReplaceDialog(options)) {
			appDisplay.replaceDialog(statusDialog)
		} else {
			appDisplay.dialog(statusDialog)
		}
		const start = new Date()
		options.excelFn().then(() => {
			// https://github.com/SpiderStrategies/Scoreboard/issues/36540#issuecomment-812717743
			const moreThan10sElapsed = new Date() - start > 10000
			if (moreThan10sElapsed) {
				appDisplay._display.view.remove()
				statusDialog.markForReload()
			}
			statusDialog.downloadReady()
			console.timeEnd('_downloadExcel')
		}).catch(e => {
			statusDialog.remove()
			appDisplay.replaceDialog(new ServerErrorDetailsDialog({
				title: i18n.get(`common.export.failed`),
				banner: i18n.get(`common.export.failed.message`),
				stackTrace: e.stack
			}))
		})
	},

	/**
	 * CSV export generated on the server.
	 *
	 * Right now this is just to support sql console because it proved to
	 * big for generating on the client.
	 *
	 * @private
	 */
	_downloadCSV(options) {
		const statusDialog = new ExportStatusDialog(options)
		appDisplay.replaceDialog(statusDialog)

		options.csvDownloadFn().then(() => {
			statusDialog.downloadReady()
		}).catch(xhr => {
			statusDialog.remove()
			// hint: Xhr.download already reads the Blob for error information
			handleError(xhr)
		})
	},

	/**
	 * @param {String} url relative path starting with /api
	 * @param {String} options.method GET|POST
	 * @param {Object} options.state the state object that should per persisted and
	 * re-applied to the export (captures unsaved changes)
	 * @param {Boolean} options.forPrint
	 */
	startServerDownload(url, options = {}) {
		const {state, method = 'GET'} = options
		const xhr = new Xhr()

		const statusDialog = new ExportStatusDialog(options)
		appDisplay.replaceDialog(statusDialog)

		xhr.download({
			url,
			method: _.isNil(state) ? method : 'POST',
			data: _.isNil(state) ? undefined : JSON.stringify(state),
			success: (data) => {
				if (xhr.status === HttpStatus.ACCEPTED) { // Long Running Job
					this._readErrorMessage(xhr, options).then(({exportId, title, message}) => {
						statusDialog.notReady(exportId, title, message)
					})
				} else { // We received a file download
					statusDialog.downloadReady()
					if (options.forPrint) {
						window.open(URL.createObjectURL(data))
					} else {
						FileSaver.saveAs(data, xhr.getFilename())
					}
				}
			},
			error: () => {
				statusDialog.remove()
				handleError(xhr)
			}
		})
	},

	_readErrorMessage(xhr, options = {}) {
		const {forPrint} = options
		return new Promise(res => {
			return xhr.readErrorResponse().then(responseJSON => {
				let {exportId, messageCode, stackTrace} = responseJSON
				let titleCode = 'common.export.error.title'
				if (messageCode === 'common.export.notready.message') {
					messageCode += forPrint ? '.forPrint' : ''
					titleCode = 'common.export.notready.title'
				}
				if (forPrint) {
					titleCode += '.forPrint'
				}
				const message = i18n.get(messageCode)
				res({
					exportId,
					title: i18n.get(titleCode),
					message,
					stackTrace,
					messageCode
				})
			})
		})
	},

	/**
	 * Opens an e-mail dialog to send a PDF export of the resource to one or more
	 * recipients.
	 */
	email(options) {
		showEmailForm(options)
	},

	/**
	 * Same as sendToEmail() but sends an entire briefing
	 */
	emailBriefing(options) {
		showEmailForm(options, '/slides')
	},

	scorecardStructureImportFile() {
		this.handleFileResponse(`scorecards/${spectator.getCurrentId()}/structure?periodId=${globals.periodId}`)
	},

	/**
	 * Issue XHR request that is expecting a file back which should be
	 * downloaded for the user.
	 *
	 * Can't use JQuery, doesn't support binary data types
	 * http://www.henryalgus.com/reading-binary-files-using-jquery-ajax/
	 *
	 * @param {String} url The url path after 'api/'
	 *
	 * @param {String} [method='GET'] GET, POST, PUT, etc
	 *
	 * @param {String} [data] JSON String to send
	 *
	 * @param {Backbone.View} [view] If a view is passed done will be triggered on
	 *     it when the response is received.
	 */
	handleFileResponse(url, options) {

		const opts = options || {}
		const xhr = new Xhr()
		xhr.download({
			url:  `api/${url}`,
			method: _.get(opts, 'method', 'GET'),
			data: opts.data,
			error: (jsonData) => errorHandler(xhr, {
				showEmptyState: false,
				jsonData,
				handler: () => {
					if (jsonData.messageCode === 'scorecards.error.invalidExportStructure') {
						jsonData.title = 'scorecards.error.invalidExportStructure.title'
						delete jsonData.stackTrace
						return false // still apply the default handler
					}
				}
			}),
			done: () => opts.view && opts.view.trigger('done')
		})
	}
}

function _isReplaceDialog(options = {}) {
	return _.isNil(options.replaceDialog) ? true : options.replaceDialog
}

/**
 * Calls the `uiState` function if passed by the view triggering the export,
 * and assigns the resulting state object back to `options`.
 *
 * @return the state object to be persisted
 */
function _setState(options) {
	if (options.uiState) {
		options.state = options.uiState({
			export: true,
			scheduled: options.scheduled
		})
	}
	return options.state
}


function showEmailForm (options, chopAfter) {
	let view
	if (license.export) {
		view = new SendToEmailForm({
			item: _exportLabel(),
			section: _.result(options, 'section', conductor.section())
		})
		view.once('done', data => _sendEmail(data, options, chopAfter))
	} else {
		view = licenseRestriction.exportRestriction()
	}
	return appDisplay.dialog(view)
}

/**
 * @param {array} data.recipients e-mail address
 * @param {string} data.message e-mail message to go in the body
 * @param {String} [chopAfter] chop the url after this string
 * @see #prompt for options documentation
 */
function _sendEmail(data, options, chopAfter) {
	var exportOpts = {
		domainId:  _.result(options, 'domainId', conductor.id()),
		url: _buildExportUrl(chopAfter, 'pdf', options, {label: false}),
		label: _exportLabel(),
		periodId: globals.periodId,
		section: _.result(options, 'section', conductor.section()),
		state: _setState(options)
	}
	var postData = _.extend(exportOpts, data)
	$.ajax({
		method: 'post',
		url: 'api/email/export',
		data: JSON.stringify(postData),
		error: _.partial(handleError, _, false),
		success: () => {
			appDisplay.dialog(new SendToEmailConfirmation({
				item: postData.label,
				totalRecipients: _.concat(postData.userIds, postData.emails)
					.length
			}))
		}
	})
}

/**
 *
 * @param {String} chopAfter
 * @param {String} type
 * @param {Object} options see `prompt`
 * @param {Object} [options.state] will be defined if this is an unsaved export
 * @param {Boolean} [labelOpts.label=true]
 * @return {string}
 * @private
 */
function _buildExportUrl(chopAfter, type, options = {}, labelOpts = {label: true}) {
	const backboneUrl = Backbone.history.getPath(Backbone.history.location.pathname)
	const normalizedUrl = _.trimEnd(backboneUrl, '/')
	const scoreboardUrl = _.defaultTo(_.result(options, 'exportURL'), normalizedUrl)
	var urlObj = url.parse(scoreboardUrl)
	// Ignore any queryString params in the url which would only be passed
	// from an unauthenticated url (i.e. e-mail) that requires a database/period.
	// Once a session is established we know the database, and we want to use
	// the global period id for the export.
	var params = {
		// #27358 - Exports must be based off of the application's period
		// It might seem backwards, but briefer.periodId is the global periodId.
		// When a briefing is showing globals.periodId is the period for that
		// specific slide (could be relative).
		periodId: briefer.isShowingBriefing() ? briefer.periodId : globals.periodId,
		type: type,
		options: _.join(_.result(options, 'options', []), ';')
	}

	const path = _chop(urlObj.pathname, chopAfter)
	const label = labelOpts.label ? `/${encodeURIComponent(_exportLabel())}` : ''
	const queryString = '?' + $.param(params)

	return `api/${path}/export${label}${queryString}`
}

/**
 * @param {string} url to examine for the chopAfter token
 * @param {string} chopAfter remove everything to the right of this string,
 *     inclusive
 */
function _chop(url, chopAfter) {
	if (chopAfter && url.indexOf(chopAfter) > 0) {
		url = url.substring(0, url.indexOf(chopAfter))
	}
	return url
}

/**
 * @param {String} [options.labelText] For testing only
 * @param {Boolean} [options.addPeriod=true] set to false if the period should
 * not be appended after the label (e.g. scheduled exports have their own period)
 */
function _exportLabel(options = {addPeriod: true}) {
	var label = options.labelText ||
		$('.briefing-label').text() || // Briefing Label has precedence over slide label
		$('.header-container > h1').text() ||
		i18n.get('common.export')

	// #21964 - incase there are multiple period displays just use the first visible one
	var period = $('.period-display:visible .text').first().text()
	if (period && options.addPeriod) {
		label += ' - ' + period
	}
	// all these characters will mess up the API route
	// % is prohibited by the spring security firewall (#26376)
	return label.replace(/[/+;%\\]/g, '_')
}

export default sendToExport
export { _exportLabel }
export { _buildExportUrl }
/**
 * How long to wait after the iFrame.onload event when capturing embedded urls
 * @type {number} milliseconds
 */
const EMBEDDED_URL_LOAD_DELAY = 15000
export { EMBEDDED_URL_LOAD_DELAY }

sendToExport.__filename__ = 'components/export/send-to-export.js'
